"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import LogoWall from "@/app/(public)/shared/components/LogoWall";
import Testimonial from "@/app/(public)/shared/components/Testimonial";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import { Button } from "@/app/(public)/shared/ui/Button";
import Image from "next/image";
import startupsGraphic from "@/app/(public)/shared/assets/graphics/startups.png";
import { CheckCircle } from "lucide-react";
import { motion } from "framer-motion";

export default function Startups() {
  const { openModal, closeModal, isModalOpen } = useModalManager();

  return (
    <main className="bg-landing-primary">
      {/* Hero Section */}
      <section className="min-h-[calc(100vh-64px)] lg:h-[calc(100vh-64px)] py-8 mt-[64px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 h-full">
          <div className="flex flex-col justify-center gap-8 md:gap-16 lg:gap-24">
            <div className="flex flex-col gap-6">
              <h1 className="text-h1 text-landing-primary">The one-stop shop for your company retreat.</h1>
              <p className="text-body-lg text-landing-secondary max-w-530">
                Whether you're planning a 3-day recharge or a week-long strategy sprint, Nowadays makes offsite planning
                effortless - from venue sourcing to activity booking.
              </p>
              <div className="flex">
                <Button variant="primary" color="primary" size="md" onClick={() => openModal("demo")}>
                  Plan my company retreat
                </Button>
              </div>
            </div>

            {/* Static Logo Wall */}
            <LogoWall
              variant="small-static"
              type="startup-small"
              trustedByText="Trusted by startups like"
              className="justify-start"
            />
          </div>
          <div className="flex items-center justify-center lg:h-full mt-8 lg:mt-0">
            <div className="w-full max-w-lg lg:max-w-none">
              <Image
                src={startupsGraphic}
                alt="Startups solution illustration"
                className="w-full h-auto object-contain rounded-2xl"
                priority
              />
            </div>
          </div>
        </div>
      </section>

      {/* All-In-One Offsite Planning Section - Document Style */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto flex flex-col gap-16">
          {/* Section Header */}
          <div className="">
            <h3 className="text-h3 text-landing-primary mb-6">All-In-One Offsite Planning</h3>
            <p className="text-body-lg text-landing-secondary max-w-2xl">
              Everything you need for an unforgettable team retreat, handled seamlessly in one place.
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h4 className="text-h4 text-landing-primary">Venues & Accommodations</h4>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Curated properties designed for team bonding and productivity. We handpick venues that inspire
                creativity and foster collaboration, from cozy mountain lodges to modern retreat centers that perfectly
                balance work and relaxation.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h4 className="text-h4 text-landing-primary">Activities & Experiences</h4>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Team-building, local adventures, wellness sessions & full itinerary planning. Create memorable shared
                experiences that strengthen bonds, spark innovation, and energize your team for the challenges ahead.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h4 className="text-h4 text-landing-primary">Transportation & Catering</h4>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Airport transfers, on-site shuttles, and local F&B vendors. We handle all the logistics so your team can
                focus on what matters most - connecting, collaborating, and building something amazing together.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h4 className="text-h4 text-landing-primary">Event Planning Support</h4>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                White-glove assistance from start to finish. Our experienced planners understand startup culture and
                move at startup speed, ensuring every detail is perfect while you focus on leading your team.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h4 className="text-h4 text-landing-primary">Pre-built Packages</h4>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Want it all handled? Our startup retreat packages include flights, venue, ground transportation,
                activities, and full itinerary planning. Perfect for fast-moving teams who need results without the
                research. All done-for-you, so you can focus on what matters most - building your company.
              </p>
            </div>
          </div>

          {/* Why Startups Love Nowadays - Card Style */}
          <div className="flex flex-col items-center gap-12">
            <h3 className="text-h3 text-landing-primary text-center">Why Startups Love Nowadays</h3>

            <div className="flex flex-col gap-6 max-w-3xl mx-auto w-full">
              <div className="rounded-xl border-2 border-violet-100 p-6 hover:border-violet-300 hover:bg-white transition-all duration-300 hover:shadow-lg flex flex-col">
                <div className="flex flex-col gap-2">
                  <h4 className="text-h4 text-landing-primary">Fast & Easy Booking</h4>
                  <p className="text-body text-landing-secondary">
                    Send requests in minutes. Contract vendors within a week. Our streamlined process eliminates the
                    endless back-and-forth typically required for event planning. Get your team offsite faster so you
                    can focus on what matters most - building your business.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border-2 border-violet-100 p-6 hover:border-violet-300 hover:bg-white transition-all duration-300 hover:shadow-lg flex flex-col">
                <div className="flex flex-col gap-2">
                  <h4 className="text-h4 text-landing-primary">Curated, Vetted Venues</h4>
                  <p className="text-body text-landing-secondary">
                    Every venue in our network is handpicked for comfort, creativity, and offsite success. We've done
                    the research and vetting so you don't have to. From inspiring mountain retreats to urban innovation
                    spaces, find venues that match your company culture and goals.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border-2 border-violet-100 p-6 hover:border-violet-300 hover:bg-white transition-all duration-300 hover:shadow-lg flex flex-col">
                <div className="flex flex-col gap-2">
                  <h4 className="text-h4 text-landing-primary">Expert Support</h4>
                  <p className="text-body text-landing-secondary">
                    Work 1:1 with a dedicated planner who handles all logistics—so you can focus on your team. Our
                    experienced professionals understand startup needs and move at startup speed. From dietary
                    restrictions to team dynamics, we handle every detail.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border-2 border-violet-100 p-6 hover:border-violet-300 hover:bg-white transition-all duration-300 hover:shadow-lg flex flex-col">
                <div className="flex flex-col gap-2">
                  <h4 className="text-h4 text-landing-primary">Exclusive Discounts</h4>
                  <p className="text-body text-landing-secondary">
                    Up to 50% off pre-negotiated room blocks and meeting spaces. Our partnerships and volume give you
                    access to rates typically reserved for enterprise clients. Stretch your budget further while
                    delivering premium experiences.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border-2 border-violet-100 p-6 hover:border-violet-300 hover:bg-white transition-all duration-300 hover:shadow-lg flex flex-col">
                <div className="flex flex-col gap-2">
                  <h4 className="text-h4 text-landing-primary">Transparent Pricing</h4>
                  <p className="text-body text-landing-secondary">
                    No markups. No hidden fees. Just clean, transparent pricing. You'll know exactly what you're paying
                    for and why. Budget with confidence knowing there won't be surprise costs or last-minute add-ons.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-12 sm:py-0">
        <div className="">
          <Testimonial variant="single" person="audrey" title="What Our Clients Say" />
        </div>
      </section>

      {/* Demo Modal */}
      <UnifiedModal
        type="cal"
        isOpen={isModalOpen("demo")}
        onClose={() => closeModal("demo")}
        title="Plan Your Company Retreat"
        subtitle="Schedule a call to start planning your perfect offsite"
        size="lg"
        calLink={DEMO_CALENDAR_LINK}
        fallbackText="Plan my company retreat"
      />
    </main>
  );
}
